import { addToast } from "@/components/base/toast";

declare const __APP_VERSION__: string;

const POLL_INTERVAL_MS = 60_000;

export function initVersionCheck() {
  if (import.meta.env.DEV) return;

  const handler = () => window.location.reload();
  window.addEventListener("vite:preloadError", handler);

  const currentVersion = __APP_VERSION__;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout>;

  async function check() {
    if (stopped) return;
    if (document.visibilityState === "hidden") {
      timer = setTimeout(check, POLL_INTERVAL_MS);
      return;
    }
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`);
      if (!res.ok) {
        timer = setTimeout(check, POLL_INTERVAL_MS);
        return;
      }
      const data = (await res.json()) as { version: string };
      if (data.version && data.version !== currentVersion) {
        stopped = true;
        addToast({
          variant: "warning",
          title: "New version available",
          description: "Refresh to get the latest updates.",
          action: {
            label: "Refresh",
            onPress: () => window.location.reload(),
          },
        });
        return;
      }
    } catch {
      // Ignore fetch failures (offline, network errors)
    }
    timer = setTimeout(check, POLL_INTERVAL_MS);
  }

  timer = setTimeout(check, POLL_INTERVAL_MS);

  // Check immediately when a hidden tab regains focus
  const onVisibilityChange = () => {
    if (stopped || document.visibilityState !== "visible") return;
    clearTimeout(timer);
    check();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  // Cleanup for React strict mode / unmount
  return () => {
    stopped = true;
    clearTimeout(timer);
    window.removeEventListener("vite:preloadError", handler);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
